import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { MapPin, Trophy, Users } from "lucide-react";

const TABS = [
  { id: "all",  label: "OVERALL",  desc: "All activities combined" },
  { id: "run",  label: "RUNNING",  desc: "Distance from runs only" },
  { id: "walk", label: "WALKING",  desc: "Distance from walks only" },
];

// Pre-computed labels for the current and previous calendar months
function monthLabel(date) {
  return date.toLocaleString(undefined, { month: "short", year: "2-digit" }).toUpperCase();
}

function getPeriodOptions() {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return [
    { id: "all",  label: "ALL TIME",                       desc: "Lifetime totals across every activity" },
    { id: "this", label: `THIS MONTH · ${monthLabel(now)}`, desc: "Resets on the 1st of every month" },
    { id: "last", label: `LAST MONTH · ${monthLabel(lastMonth)}`, desc: "Previous calendar month archive" },
  ];
}

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [cityRows, setCityRows] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("ALL");
  const [type, setType] = useState("all");
  const [period, setPeriod] = useState("all");

  const PERIOD_OPTIONS = getPeriodOptions();

  useEffect(() => {
    api.get("/leaderboard/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (city && city !== "ALL") params.set("city", city);
    if (type && type !== "all") params.set("activity_type", type);
    if (period && period !== "all") params.set("period", period);
    const q = params.toString() ? `?${params.toString()}` : "";
    api.get(`/leaderboard${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));

    const cityParams = new URLSearchParams();
    if (type && type !== "all") cityParams.set("activity_type", type);
    if (period && period !== "all") cityParams.set("period", period);
    const cityQ = cityParams.toString() ? `?${cityParams.toString()}` : "";
    api.get(`/leaderboard/cities/stats${cityQ}`).then((r) => setCityRows(r.data)).catch(() => setCityRows([]));
  }, [city, type, period]);

  const activeTab = TABS.find((t) => t.id === type);
  const activePeriod = PERIOD_OPTIONS.find((p) => p.id === period);
  const kmLabel = type === "walk" ? "WALK KM" : type === "run" ? "RUN KM" : "KM";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12" data-testid="leaderboard-page">
      <SEO title="OSCR Leaderboard" path="/leaderboard"
        description="Live leaderboard of Odisha runners — total kilometres logged across all OSCR challenges. See how runners from Rourkela, Bhubaneswar, Cuttack, Puri and beyond rank statewide." />
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">RANK</p>
      <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">Leaderboard</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl leading-relaxed">
        Total kilometres logged across all OSCR challenges. Every runner on this board is part of
        the pan-Odisha movement — from a Berhampur beginner logging her first 5K to a Rourkela
        ultra-marathoner chasing 100 km a month. Friendly competition, real motivation, one state
        running as one pack.
      </p>

      {/* Activity-type tabs (drive both tables) */}
      <div className="mt-8 hairline bg-zinc-900 inline-flex flex-wrap" data-testid="leaderboard-type-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setType(t.id)}
            data-testid={`leaderboard-tab-${t.id}`}
            className={`px-5 py-2.5 font-display tracking-[0.25em] text-xs transition border-r border-zinc-800 last:border-r-0 ${
              type === t.id
                ? "bg-orange-600 text-white"
                : "text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeTab && (
        <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
          {activeTab.desc}
        </p>
      )}

      {/* Period selector (resets monthly) */}
      <div className="mt-5 hairline bg-zinc-900 inline-flex flex-wrap" data-testid="leaderboard-period-tabs">
        {PERIOD_OPTIONS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            data-testid={`leaderboard-period-${p.id}`}
            className={`px-4 py-2 font-display tracking-[0.2em] text-[11px] transition border-r border-zinc-800 last:border-r-0 ${
              period === p.id
                ? "bg-white text-black"
                : "text-zinc-400 hover:text-white hover:bg-zinc-800"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {activePeriod && (
        <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">
          {activePeriod.desc}
        </p>
      )}

      {/* RUNNER LEADERBOARD */}
      <section className="mt-8" data-testid="runner-leaderboard">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase flex items-center gap-1">
              <Trophy size={11} /> RUNNERS
            </p>
            <h2 className="font-display text-2xl text-white tracking-wider mt-1">Pack leaders</h2>
          </div>

          {/* City filter (runner table only) */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-zinc-400">
              <MapPin size={14} className="text-orange-500" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase">FILTER BY CITY</span>
            </div>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="leaderboard-city-filter" className="w-[220px] rounded-none bg-zinc-900 border-zinc-800 text-white focus:ring-orange-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none max-h-72">
                <SelectItem value="ALL" className="rounded-none focus:bg-orange-600 focus:text-white">All of Odisha</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c} value={c} className="rounded-none focus:bg-orange-600 focus:text-white">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
              {rows.length} RUNNER{rows.length !== 1 ? "S" : ""}
            </span>
          </div>
        </div>

        <div className="hairline bg-zinc-900 divide-y divide-zinc-800">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 font-mono text-sm">
              {type === "walk" ? "No walkers on the board yet — log a walk to claim #1." : type === "run" ? "No runners on the board yet — log a run to claim #1." : "No runners on the board yet."}
            </div>
          ) : rows.map((r, i) => (
            <Link key={r.user_id} to={`/profile/${r.user_id}`} className="p-5 flex items-center gap-4 hover:bg-zinc-900/60 transition">
              <span className={`font-display text-3xl w-10 ${i === 0 ? "text-orange-500" : i < 3 ? "text-orange-400" : "text-zinc-500"}`}>{i + 1}</span>
              <Avatar className="h-10 w-10 rounded-none">
                <AvatarImage src={r.picture} />
                <AvatarFallback className="rounded-none bg-zinc-800 text-white font-display">
                  {r.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-white">{r.name}</p>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                  {r.city ? <span className="text-orange-500">{r.city}</span> : "—"}
                  {(r.badges || []).length > 0 && <span> · {(r.badges || []).length} BADGES</span>}
                </p>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-orange-500">{r.total_km || 0}</div>
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{kmLabel}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CITY LEADERBOARD */}
      <section className="mt-12" data-testid="city-leaderboard">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase flex items-center gap-1">
              <MapPin size={11} /> CITIES & DISTRICTS
            </p>
            <h2 className="font-display text-2xl text-white tracking-wider mt-1">District / city</h2>
            <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-1">
              Total km logged by each city / district · who is leading Odisha?
            </p>
          </div>
          <span className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
            {cityRows.length} CIT{cityRows.length === 1 ? "Y" : "IES"}
          </span>
        </div>

        <div className="hairline bg-zinc-900 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-800 bg-zinc-950">
            <div className="col-span-1 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">#</div>
            <div className="col-span-6 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">CITY / DISTRICT</div>
            <div className="col-span-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase text-right hidden sm:block">RUNNERS</div>
            <div className="col-span-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase text-right hidden sm:block">AVG / RUNNER</div>
            <div className="col-span-3 sm:col-span-1 font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase text-right">{kmLabel}</div>
          </div>

          {cityRows.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 font-mono text-sm">
              No city / district has activity yet. Be the first to put your home on the map.
            </div>
          ) : cityRows.map((r, i) => (
            <button
              key={r.city}
              onClick={() => setCity(r.city)}
              data-testid={`city-row-${r.city}`}
              className="w-full grid grid-cols-12 px-5 py-4 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-900/60 transition text-left"
              title={`Filter runners by ${r.city}`}
            >
              <div className="col-span-1 flex items-center">
                <span className={`font-display text-2xl ${i === 0 ? "text-orange-500" : i < 3 ? "text-orange-400" : "text-zinc-500"}`}>
                  {i + 1}
                </span>
              </div>
              <div className="col-span-6 flex items-center gap-2 min-w-0">
                <span className="font-display text-lg text-white truncate">{r.city}</span>
                {i === 0 && <Trophy size={14} className="text-orange-500 shrink-0" />}
              </div>
              <div className="col-span-2 hidden sm:flex items-center justify-end gap-1 text-zinc-300">
                <Users size={11} className="text-zinc-500" />
                <span className="font-mono text-sm">{r.runners}</span>
              </div>
              <div className="col-span-2 hidden sm:flex items-center justify-end font-mono text-sm text-zinc-300">
                {r.avg_km}
              </div>
              <div className="col-span-3 sm:col-span-1 flex items-center justify-end">
                <span className="font-display text-xl text-orange-500">{r.total_km}</span>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
